import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import { createCloudWorkspaceClient } from '@/lib/cloud-runtime/cloud-workspace-client';
import { localWorkspaceServer } from '@/lib/local-runtime';
import type { WorkspaceClient } from '@/lib/workspaces/workspace-client';

/** Connection details for the local server's workspace server. */
export type LocalWorkspaceServer = { url: string; token: string };

/**
 * Builds a {@link WorkspaceClient} for a locally-spawned Crowdex CLI workspace
 * server. The desktop's local server speaks the exact same WebSocket frame
 * protocol as the cloud runtime, so this reuses {@link createCloudWorkspaceClient}
 * with a synthesized runtime: only the loopback `url`, per-launch `token`, and
 * the desktop's absolute `workspaceRoot` differ. This is what makes "web and
 * desktop connect to the CLI the same way" true for the unified op surface.
 */
export function createLocalWorkspaceClient(
  server: LocalWorkspaceServer,
  workspaceRoot: string,
): WorkspaceClient {
  const runtimeFor = (current: LocalWorkspaceServer): CloudRuntime => ({
    id: 'local-workspace',
    providerId: 'local',
    profileId: 'local',
    name: 'Local workspace',
    url: current.url,
    token: current.token,
    workspaceRoot,
    workspaceIdentity: `path:${workspaceRoot}`,
    agentId: 'local',
    created: false,
    resumed: false,
  });
  return createCloudWorkspaceClient(runtimeFor(server), () => {
    const current = localWorkspaceServer();
    if (current === null) throw new Error('The local workspace server is not ready.');
    return runtimeFor(current);
  });
}
